// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import type { Theme } from '@mui/material';

/**
 * Asset transition phases for asset transition reporting.
 */
export type AssetTransitionPhase =
  | 'start'
  | 'added'
  | 'totalAvailable'
  | 'sold'
  | 'deployed'
  | 'depreciated'
  | 'scrapped'
  | 'end';

/**
 * Phase configuration for asset transition reporting.
 */
interface AssetTransitionPhaseConfig {
  displayName: string;
  color: (theme: Theme) => string;
  summaryTitle: string;
  summarySubtitle: string;
  description: string;
}

/**
 * Complete phase definitions with metadata.
 *
 * This provides a single source of truth for all phase-related data
 * while maintaining type safety through the AssetTransitionPhase union type.
 */
export const ASSET_TRANSITION_PHASES: Record<
  AssetTransitionPhase,
  AssetTransitionPhaseConfig
> = {
  start: {
    displayName: 'Start',
    summaryTitle: 'Starting inventory',
    summarySubtitle: 'at period start',
    color: (theme) => theme.palette.primary.light,
    description: 'Assets available at the beginning of the period',
  },
  added: {
    displayName: 'Added',
    summaryTitle: '+ Added',
    summarySubtitle: 'added',
    color: (theme) => theme.palette.success.main,
    description: 'New assets acquired during the period',
  },
  totalAvailable: {
    displayName: 'Total available',
    summaryTitle: '= Total available',
    summarySubtitle: 'for period',
    color: () => '#000000',
    description: 'Total assets available for deployment or sale',
  },
  sold: {
    displayName: 'Sold',
    summaryTitle: '- Sold',
    summarySubtitle: 'sold',
    color: (theme) => theme.palette.warning.light,
    description: 'Assets sold during the period',
  },
  deployed: {
    displayName: 'Deployed',
    summaryTitle: '- Deployed',
    summarySubtitle: 'to fixed assets',
    color: (theme) => theme.palette.grey[500],
    description: 'Assets deployed to customers during the period',
  },
  depreciated: {
    displayName: 'Depreciated',
    summaryTitle: '- Depreciated',
    summarySubtitle: 'depreciated',
    color: (theme) => theme.palette.grey[500],
    description: 'Fixed assets that depreciated in value during the period',
  },
  scrapped: {
    displayName: 'Scrapped',
    summaryTitle: '- Scrapped',
    summarySubtitle: 'scrapped',
    color: (theme) => theme.palette.error.main,
    description: 'Assets scrapped during the period',
  },
  end: {
    displayName: 'End',
    summaryTitle: '= Ending inventory',
    summarySubtitle: 'at period end',
    color: (theme) => theme.palette.primary.main,
    description: 'Assets remaining at the end of the period',
  },
};
